import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Box,
  Chip,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  InputAdornment,
  Paper,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ScienceIcon from "@mui/icons-material/Science";
import clinicalTrialsService from "../../services/clinicalTrialsService";
import TrialCard from "../../components/ClinicalTrials/TrialCard";

const TrialBrowsePage = () => {
  // State Management
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filter State
  const [filters, setFilters] = useState({
    keyword: "",
    ckdStage: "",
    status: "",
    phase: "",
    targetOrgan: "",
    sortBy: "updated_desc",
  });

  // Filter Options
  const ckdStages = ["1", "2", "3a", "3b", "4", "5", "5D"];

  const statuses = [
    "RECRUITING",
    "NOT_YET_RECRUITING",
    "ACTIVE_NOT_RECRUITING",
    "COMPLETED",
    "SUSPENDED",
    "TERMINATED",
    "WITHDRAWN",
  ];

  const phases = ["EARLY_PHASE1", "PHASE1", "PHASE2", "PHASE3", "PHASE4", "NA"];

  const organs = [
    "Cardiac",
    "Renal",
    "Neurological",
    "Hepatic",
    "GI",
    "Pulmonary",
    "Cutaneous",
    "Multi-organ",
  ];

  const sortOptions = [
    { value: "updated_desc", label: "Recently Updated" },
    { value: "updated_asc", label: "Oldest First" },
    { value: "title_asc", label: "Title (A-Z)" },
    { value: "title_desc", label: "Title (Z-A)" },
    { value: "status", label: "Status" },
  ];

  // Fetch Trials
  useEffect(() => {
    fetchTrials();
  }, [filters, page]);

  const fetchTrials = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = {
        page: page - 1, // Backend expects 0-indexed
        size: 20,
      };

      // Add filters if they exist
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.ckdStage) params.ckdStage = filters.ckdStage;
      if (filters.status) params.status = filters.status;
      if (filters.phase) params.phase = filters.phase;
      if (filters.targetOrgan) params.targetOrgan = filters.targetOrgan;

      console.log("🔍 Fetching trials with params:", params);

      const response = await clinicalTrialsService.getAllTrials(params);

      console.log("📊 Trials response:", response);

      if (response.success) {
        setTrials(response.data.content || response.data.trials || []);
        setTotalCount(
          response.data.totalElements || response.data.totalCount || 0
        );
        setTotalPages(
          response.data.totalPages ||
            Math.ceil((response.data.totalCount || 0) / 20)
        );
      }
    } catch (err) {
      console.error("❌ Error fetching trials:", err);
      setError("Failed to load trials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({
      keyword: "",
      ckdStage: "",
      status: "",
      phase: "",
      targetOrgan: "",
      sortBy: "updated_desc",
    });
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(
    (v) => v && v !== "updated_desc"
  ).length;

  const formatStatus = (status) => {
    return status.replace(/_/g, " ");
  };

  const formatPhase = (phase) => {
    if (phase === "NA") return "N/A";
    if (phase === "EARLY_PHASE1") return "Early Phase 1";
    return phase.replace("PHASE", "Phase ");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "RECRUITING":
        return "success";
      case "NOT_YET_RECRUITING":
        return "info";
      case "COMPLETED":
        return "default";
      case "ACTIVE_NOT_RECRUITING":
        return "warning";
      default:
        return "error";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <ScienceIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Browse Clinical Trials
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Explore all amyloidosis clinical trials in our database
        </Typography>
      </Box>

      {/* Filters Section */}
      <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterListIcon />
              <Typography variant="h6">Filters</Typography>
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} active`}
                  size="small"
                  sx={{ bgcolor: "white", color: "#1A5EA8" }}
                />
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={activeFilterCount === 0}
              sx={{
                color: "white",
                borderColor: "white",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Clear All
            </Button>
          </Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* Keyword Search */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search by keyword"
                value={filters.keyword}
                onChange={(e) => handleFilterChange("keyword", e.target.value)}
                placeholder="e.g., 'tafamidis', 'cardiac', 'NCT123456'"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Sort By */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy}
                  label="Sort By"
                  onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* CKD Stage */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>CKD Stage</InputLabel>
                <Select
                  value={filters.ckdStage}
                  label="CKD Stage"
                  onChange={(e) =>
                    handleFilterChange("ckdStage", e.target.value)
                  }
                >
                  <MenuItem value="">All Stages</MenuItem>
                  {ckdStages.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {formatStatus(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Phase */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Phase</InputLabel>
                <Select
                  value={filters.phase}
                  label="Phase"
                  onChange={(e) => handleFilterChange("phase", e.target.value)}
                >
                  <MenuItem value="">All Phases</MenuItem>
                  {phases.map((phase) => (
                    <MenuItem key={phase} value={phase}>
                      {formatPhase(phase)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Target Organ */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Target Organ</InputLabel>
                <Select
                  value={filters.targetOrgan}
                  label="Target Organ"
                  onChange={(e) =>
                    handleFilterChange("targetOrgan", e.target.value)
                  }
                >
                  <MenuItem value="">All Organs</MenuItem>
                  {organs.map((organ) => (
                    <MenuItem key={organ} value={organ}>
                      {organ}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      {/* Results Summary */}
      {!loading && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Showing <strong>{trials.length}</strong> of{" "}
            <strong>{totalCount}</strong> trials
            {activeFilterCount > 0 && ` (filtered)`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Page {page} of {totalPages}
          </Typography>
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading trials...
          </Typography>
        </Box>
      )}

      {/* Trials Grid */}
      {!loading && trials.length > 0 && (
        <Grid container spacing={3}>
          {trials.map((trial) => (
            <Grid item xs={12} key={trial.nctId || trial.id}>
              <TrialCard trial={trial} source="internal" />
            </Grid>
          ))}
        </Grid>
      )}

      {/* No Results */}
      {!loading && trials.length === 0 && (
        <Paper
          elevation={2}
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            bgcolor: "background.default",
          }}
        >
          <ScienceIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No trials found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Try adjusting your filters or search criteria
          </Typography>
          <Button
            variant="contained"
            onClick={clearFilters}
            startIcon={<ClearIcon />}
          >
            Clear All Filters
          </Button>
        </Paper>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Container>
  );
};

export default TrialBrowsePage;
